import { DiscountsPageContent } from './DiscountsLayoutPage';
import { Card, Group, useMantineTheme, Box, Title } from '@mantine/core';
import { FillerSwitch } from '@root/Design/Filler';
import { useEffect, useState } from 'react';
import { useDi } from '@root/Services/DI';
import { BarChart, UserConfiguredBarChartSettings } from '@root/Components/Charts/BarChart';
import { DiscountQueryService } from '@root/Services/Invoices/DiscountQueryService';
import { RangeSelection } from './DiscountsPage';
import { queryBuilder } from '@root/Services/QueryExpr';
import { MdsHistoricalRecord, MdsHistoricalRecordCommitmentTerm } from '@apis/Invoices/model';

export function Coverage(props: { platform: string; range: RangeSelection }) {
    const { colors } = useMantineTheme();
    const { platform, range } = props;
    const [chartData, setChartData] = useState<{ date: string; type: string; value: number; color: string }[]>([]);
    const discountQueryService = useDi(DiscountQueryService);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchDiscountData();
    }, [platform, range.inclFrom, range.inclTo]);

    const fetchDiscountData = async () => {
        try {
            const { inclFrom, inclTo, fromMonth, toMonth } = props.range;
            const discountApi = discountQueryService.scopeApi(platform, fromMonth, toMonth);

            let query = queryBuilder<MdsHistoricalRecord>();

            if (props.platform !== 'All') {
                query = query.where((b: any) => b.model.Platform!.eq(props.platform));
            }

            const rawResp = await query
                .select((b) => ({
                    date: b.truncDate('month', b.model.Date! as unknown as Date, 0, b.param(inclFrom), b.param(inclTo)),
                    type: b.model.CommitmentTerm!,
                    value: b.sum(b.model.NetSavings ?? 0),
                }))
                .execute(discountApi);
            const data = rawResp?.Results ?? [];
            const ufTermName: Record<MdsHistoricalRecordCommitmentTerm, { label: string; color: string; sort: number }> = {
                InsuranceBacked30Day: { label: '30-Day IBD', color: '#047FAF99', sort: 4 },
                InsuranceBacked1Year: { label: '1 Year IBD', color: '#0375C1', sort: 3 },
                Standard1Year: { label: '1 Year Std', color: '#AF510499', sort: 2 },
                Standard3Year: { label: '3 Year Std', color: '#AF5104', sort: 1 },
                None: { label: 'Uncovered', color: '#D9D9D9', sort: 0 },
            };
            const chartData = data.map((d) => ({
                date: d.date as unknown as string,
                type: ufTermName[d.type as MdsHistoricalRecordCommitmentTerm]?.label || 'Unknown',
                value: d.value ?? 0,
                color: ufTermName[d.type as MdsHistoricalRecordCommitmentTerm]?.color || '#D9D9D9',
                sort: ufTermName[d.type as MdsHistoricalRecordCommitmentTerm]?.sort || 0,
            }));
            chartData.sort((a, b) => a.sort - b.sort);
            setChartData(chartData);
        } finally {
            setLoading(false);
        }
    };

    return (
        <FillerSwitch loading={loading} noData={!chartData?.length} noDataMessage="No coverage data found.">
            {() => (
                <DiscountsPageContent>
                    <Card withBorder p={0} radius="md" shadow="sm" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ borderBottom: `solid 1px ${colors.gray[3]}}` }}>
                            <Title py="xs" px="lg" mb={0} order={4}>
                                Commitment Coverage Sources
                            </Title>
                        </Box>
                        <Box sx={{ flex: 1, minHeight: 0 }}>
                            <DiscountsCoverageBar data={chartData} />
                        </Box>
                    </Card>
                </DiscountsPageContent>
            )}
        </FillerSwitch>
    );
}

interface DiscountsCoverageBarProps {
    data: { date: string; type: string; value: number; color: string }[];
}

function DiscountsCoverageBar(props: DiscountsCoverageBarProps) {
    const { data } = props;
    const barSettings = {
        orientation: 'Vertical',
        format: 'money',
        xFormat: 'short-month',
        margin: { top: 0 },
        reaggOptions: {
            sortBy: 'label',
            sortDir: 'asc',
        },
        chartColors: ({ dimensions }) => data.find((d) => d.type === dimensions[1])?.color || '#009FE1',
    } as UserConfiguredBarChartSettings;
    return <BarChart settings={barSettings} groups={['date', 'type']} values={['value']} data={data} mode="user-configured" />;
}
